import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api, { setAuthToken, authReady } from "@/lib/api";

const AuthContext = createContext(null);
const CACHED_USER_KEY = "em_user_v1";

// Read the last-known user object from localStorage so that on PWA / mobile
// cold restart we can render the dashboard optimistically — before /auth/me
// completes — and only kick the user back to /login if the server explicitly
// says the session is invalid (401). Transient network / 5xx errors never
// log them out.
const readCachedUser = () => {
  try {
    const raw = localStorage.getItem(CACHED_USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};
const writeCachedUser = (u) => {
  try {
    if (u) localStorage.setItem(CACHED_USER_KEY, JSON.stringify(u));
    else localStorage.removeItem(CACHED_USER_KEY);
  } catch (e) { /* ignore */ }
};

export const useAuth = () => useContext(AuthContext);

// NOTIFICATIONS-01: detach this device from Web Push on logout. The server
// row is removed first (needs the still-valid session), then the browser
// subscription itself, so the push service stops accepting messages for this
// endpoint even if the server call failed (offline / expired session) — the
// backend prunes it on the next send (404/410). Never blocks logout.
const dropPushSubscription = async () => {
  try {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = reg && reg.pushManager ? await reg.pushManager.getSubscription() : null;
    if (!sub) return;
    try {
      await api.post("/notifications/push-unsubscribe", { endpoint: sub.endpoint }, { timeout: 4000 });
    } catch (e) { /* server row is pruned on the next failed send */ }
    await sub.unsubscribe();
  } catch (e) { /* push unsupported — nothing to remove */ }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => readCachedUser());
  const [loading, setLoading] = useState(true);

  // Keep the cached copy in sync every time the caller updates the user.
  const setUserSync = useCallback((u) => {
    setUser(u);
    writeCachedUser(u);
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      // Wait for any IndexedDB-stored token to be promoted back into the
      // sync stores so the request interceptor can attach a Bearer header.
      await authReady;
      const res = await api.get("/auth/me");
      setUserSync(res.data);
    } catch (e) {
      // Only invalidate the session when the SERVER explicitly says the
      // token is no longer valid. Any other error (offline, 5xx, CORS)
      // keeps the cached user intact so the app is usable.
      if (e?.response?.status === 401) {
        setUserSync(null);
      }
    } finally {
      setLoading(false);
    }
  }, [setUserSync]);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = useCallback(async () => {
    await dropPushSubscription();
    try {
      await api.post("/auth/logout");
    } catch (e) { /* ignore */ }
    setAuthToken(null);
    setUserSync(null);
  }, [setUserSync]);

  return (
    <AuthContext.Provider value={{ user, setUser: setUserSync, loading, checkAuth, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
